/**
 * 🌐 BRAUZER QURILMALAR (B-slot, 2026-10-29; backend: services/webDevices.js + consoleBrowser, docs/knowledge/195).
 * Operator: «test admin va test traccar'ni alohida brauzerda ochganman — Khorezmai uni boshqara olmaydi. Emulatorlardagidek
 * qilsak: men ham ko'rib boshqaray, Khorezmai ham boshqarsin, agentga vazifa bersam men ko'rib turay».
 *
 * /emu oynasida emulatorlar yonida turadigan UMUMIY brauzer: serverdagi haqiqiy Chrome (login saqlanadi), jonli kadr
 * (JPEG, /web-devices/:id/frame), siz bosasiz/yozasiz/aylantirasiz (/input), agent esa mcpBrowser orqali AYNAN shu
 * brauzerda ishlaydi — ramka ko'k bo'ladi, ✋ bilan boshqaruvni olasiz (emulatordagidek, run pauzasi).
 *
 *  · WebDeviceScreen — ekran + kiritish (grid katagida ham, to'liq ekranda ham).
 *  · WebDevicesSection — /emu gridi ostidagi «🌐 Brauzerlar» bo'limi: qo'shish/tahrirlash/o'chirish, ⛶ to'liq ekranga.
 *  · 📎 Fayl: brauzer serverda, shuning uchun saytning «fayl tanlash» oynasi sizda ochilmaydi — server uni ushlaydi va kadr bilan
 *    `X-Kh-Chooser` keladi → ekran ustida sariq qator; 📎 / sudrab tashlangan fayl o'sha oynaga (yoki sahifadagi fayl maydoniga) tushadi.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';

const KB_SPECIAL = ['Enter', 'Backspace', 'Delete', 'Tab', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'];
const pageVisible = () => { try { return document.visibilityState !== 'hidden'; } catch (e) { return true; } };
// ☕ /emu «Uxlatma» yoqilgan (App.js, `kh_emu_awake`) — oyna yashirin bo'lsa ham daqiqada bir kadr so'raladi:
// server bo'sh turgan brauzerni 20 daqiqada yopadi (consoleBrowser IDLE_MS), kuzatib qaytganda u ochiq tursin.
const awakeOn = () => { try { return localStorage.getItem('kh_emu_awake') === '1'; } catch (e) { return false; } };
export const wdAspect = (d) => (d && d.size && d.size.width && d.size.height ? d.size.width / d.size.height : 16 / 10);

// Agent kim va qachondan beri (App.js khAgentWho bilan bir xil ma'no).
function agentWho(ag) {
  if (!ag) return '';
  const lbl = ag.label || (ag.taskId ? `Vazifa #${ag.taskId}` : '');
  if (!ag.startedAt) return lbl;
  const m = Math.max(0, Math.round((Date.now() - Number(ag.startedAt)) / 60000));
  return `${lbl}${lbl ? ' · ' : ''}${m >= 60 ? `${Math.floor(m / 60)} soat ${m % 60} daq` : `${m} daq`} oldin boshlangan`;
}

/**
 * Bitta brauzer qurilmaning jonli ekrani. `style` — tashqi o'lcham (to'liq ekranda khFsLayout beradi); ekran ichida
 * object-contain, bosish koordinatasi rasmning HAQIQIY o'lchamiga aylantiriladi.
 */
export function WebDeviceScreen({ authFetch, apiUrl, device, agent, onAgentControl, onChanged, onRemoveFs, isMain = null, onToggleMain = null, fs = false, paused = false, style }) {
  const [frame, setFrame] = useState(null);
  const [url, setUrl] = useState(device.url || '');
  const [urlEdit, setUrlEdit] = useState(null);       // null = joriy manzil ko'rinadi; string = foydalanuvchi yozmoqda
  const [stopped, setStopped] = useState(!device.running);
  const [busy, setBusy] = useState(false);
  const [kbFocus, setKbFocus] = useState(false);
  const [chooser, setChooser] = useState(null);       // sayt fayl so'rab turibdi: {multiple, accept}
  const [pick, setPick] = useState(null);             // bir nechta fayl maydoni: {files, inputs}
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const frameRef = useRef(null);
  const fastRef = useRef(0);                          // shu vaqtgacha tez yangilash (bosishdan keyin)
  const typeBufRef = useRef('');
  const typeTimerRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  const boxRef = useRef(null);
  const agentRef = useRef(agent); agentRef.current = agent;
  const base = `${apiUrl}/web-devices/${device.id}`;

  useEffect(() => { setStopped(!device.running); }, [device.running]);

  // 🎞 Kadr tsikli: ko'rinib turganda ~0.7 s, bosishdan keyin 2 s davomida ~0.25 s. Oyna yashirin yoki shu qurilma
  // to'liq ekranda (paused — gridagi nusxa ustidan yopilgan) — to'xtaydi, server ikki marta kadr olmaydi.
  useEffect(() => {
    if (stopped || paused) return undefined;
    let alive = true, timer = null, bgAt = 0;
    const tick = async () => {
      if (!alive) return;
      const vis = pageVisible();
      if (vis || (awakeOn() && Date.now() - bgAt >= 60_000)) {
        if (!vis) bgAt = Date.now();
        try {
          const r = await authFetch(`${base}/frame`);
          if (r.status === 409) { if (alive) { setStopped(true); onChanged && onChanged(); } return; }
          if (r.ok) {
            const u = r.headers.get('X-Kh-Url');
            if (u) setUrl(decodeURIComponent(u));
            const ch = r.headers.get('X-Kh-Chooser');
            if (ch) {
              const m = /m=(\d)/.exec(ch), a = /a=([^;]*)/.exec(ch);
              const v = { multiple: !!(m && m[1] === '1'), accept: a ? decodeURIComponent(a[1]) : '' };
              setChooser(prev => (prev && prev.multiple === v.multiple && prev.accept === v.accept ? prev : v));
            } else setChooser(null);
            const blob = await r.blob();
            const obj = URL.createObjectURL(blob);
            if (alive) { setFrame(prev => { if (prev) URL.revokeObjectURL(prev); return obj; }); } else URL.revokeObjectURL(obj);
          }
        } catch (e) { /* tarmoq uzilishi — keyingi tikda */ }
      }
      if (alive) timer = setTimeout(tick, Date.now() < fastRef.current ? 250 : 700);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [stopped, paused, base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { setFrame(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); }, []);

  const post = async (path, body) => {
    const r = await authFetch(`${base}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `Xato ${r.status}`);
    return d;
  };
  const agentHint = () => {
    const ag = agentRef.current;
    if (ag && ag.active && !ag.paused) toast("🤖 Agent shu brauzerda ishlayapti — aralashmasdan oldin «✋ Boshqaruvni olish» ni bosing", { id: `wd-ag-${device.id}` });
  };
  // Kiritishlar KETMA-KET yuboriladi (yozilgan harflar aralashib ketmasin).
  const queueInput = (body) => {
    fastRef.current = Date.now() + 2000;
    const p = queueRef.current.then(() => post('/input', body)).then((d) => {
      if (d && d.url) setUrl(d.url);
      if (d && d.warn) toast(d.warn, { id: `wd-warn-${device.id}` });
      return d;
    }).catch((e) => { toast.error(String(e.message || e).slice(0, 200), { id: `wd-err-${device.id}` }); return null; });
    queueRef.current = p;
    return p;
  };
  const flushType = () => {
    clearTimeout(typeTimerRef.current); typeTimerRef.current = null;
    const t = typeBufRef.current; typeBufRef.current = '';
    if (t) queueInput({ action: 'type', text: t });
  };

  const start = async () => {
    setBusy(true);
    try { const d = await post('/start'); if (d.url) setUrl(d.url); setStopped(false); onChanged && onChanged(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { await post('/stop'); setStopped(true); onChanged && onChanged(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // ── Sichqoncha: bosish / 2-3 marta bosish / sudrab belgilash ─────────────
  const dragRef = useRef(null);
  const xy = (img, ev) => {
    // object-contain: rasm ramkada markazlangan — haqiqiy kontent to'rtburchagini hisoblaymiz.
    const rect = img.getBoundingClientRect();
    const nw = img.naturalWidth, nh = img.naturalHeight;
    const sc = Math.min(rect.width / nw, rect.height / nh);
    const cw = nw * sc, ch = nh * sc;
    const ox = rect.left + (rect.width - cw) / 2, oy = rect.top + (rect.height - ch) / 2;
    return { x: Math.round(Math.min(nw - 1, Math.max(0, (ev.clientX - ox) / sc))), y: Math.round(Math.min(nh - 1, Math.max(0, (ev.clientY - oy) / sc))) };
  };
  const onDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const img = frameRef.current;
    if (!img || !img.naturalWidth) return;
    dragRef.current = { ...xy(img, e), cx: e.clientX, cy: e.clientY };
  };
  const onUp = (e) => {
    const st = dragRef.current; dragRef.current = null;
    if (boxRef.current) boxRef.current.focus();
    const img = frameRef.current;
    if (!st || !img || !img.naturalWidth) return;
    agentHint();
    const end = xy(img, e);
    flushType();
    if (Math.hypot(e.clientX - st.cx, e.clientY - st.cy) > 6) { queueInput({ action: 'drag', x: st.x, y: st.y, x2: end.x, y2: end.y }); return; }
    if (e.detail >= 2) { queueInput({ action: 'multiclick', x: end.x, y: end.y, count: Math.min(3, e.detail) }); return; }
    queueInput({ action: 'click', x: end.x, y: end.y });
  };
  // G'ildirak — kursor ostidagi elementni suradi (passive:false, sahifa o'zi surilmasin). Yig'ib, ~120 ms da bir yuboriladi.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    let acc = 0, accX = 0, t = null, last = null;
    const on = (ev) => {
      ev.preventDefault();
      const img = frameRef.current;
      if (!img || !img.naturalWidth) return;
      last = xy(img, ev);
      acc += ev.deltaY; accX += ev.deltaX;
      if (t) return;
      t = setTimeout(() => {
        const dy = Math.round(acc), dx = Math.round(accX); acc = 0; accX = 0; t = null;
        if (dy || dx) queueInput({ action: 'wheel', x: last.x, y: last.y, dy, dx });
      }, 120);
    };
    el.addEventListener('wheel', on, { passive: false });
    return () => { el.removeEventListener('wheel', on); clearTimeout(t); };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Klaviatura (ekran fokusda bo'lsa): harflar to'planib yoziladi, maxsus tugmalar alohida ──
  const copyOut = async () => {
    try {
      const d = await post('/copy', {});
      if (!d.ok || !d.text) { toast(d.error || "Belgilangan matn yo'q"); return; }
      await navigator.clipboard.writeText(d.text);
      toast.success(`📋 Nusxalandi (${d.text.length} belgi)`);
    } catch (e) { toast.error("Nusxalab bo'lmadi"); }
  };
  const onKeyDown = (e) => {
    const k = e.key;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && (k === 'c' || k === 'C')) { e.preventDefault(); copyOut(); return; }
    if (mod && (k === 'v' || k === 'V')) return;   // «paste» hodisasi ushlaydi
    if (['Shift', 'Meta', 'Control', 'Alt', 'CapsLock'].includes(k)) return;
    if (mod && /^[axzy]$/i.test(k)) {
      e.preventDefault(); flushType(); agentHint();
      queueInput({ action: 'key', key: (e.shiftKey ? 'Shift+' : '') + 'Control+' + k.toUpperCase() });   // serverdagi brauzer Linux'da
      return;
    }
    if (mod || e.altKey) return;   // o'z brauzeringiz yorliqlari (⌘R, ⌘T…) o'zingizda qoladi
    if (KB_SPECIAL.includes(k)) {
      e.preventDefault(); flushType(); agentHint();
      queueInput({ action: 'key', key: (e.shiftKey ? 'Shift+' : '') + k });
      return;
    }
    if (k.length === 1) {
      e.preventDefault();
      typeBufRef.current += k;
      if (!typeTimerRef.current) typeTimerRef.current = setTimeout(flushType, 120);
    }
  };
  // 📎 Fayl yuklash: sayt so'rab turgan bo'lsa — o'sha oynaga; aks holda sahifadagi fayl maydoniga (bir nechta bo'lsa tanlaysiz).
  const doUpload = async (fileList, input = null) => {
    const list = Array.from(fileList || []).slice(0, 10);
    if (!list.length) return;
    const big = list.find(f => f.size > 25 * 1024 * 1024);
    if (big) { toast.error(`«${big.name}» 25 MB dan katta`); return; }
    agentHint();
    if (!chooser && input == null) {
      try {
        const r = await authFetch(`${base}/file-inputs`);
        const d = await r.json().catch(() => ({}));
        if (!d.chooser) {
          const ins = d.inputs || [];
          if (!ins.length) { toast("Sahifada fayl maydoni yo'q — avval saytning «yuklash» tugmasini bosing (sayt fayl so'rasa sariq qator chiqadi)", { id: `wd-up-${device.id}` }); return; }
          if (ins.length > 1) { setPick({ files: list, inputs: ins }); return; }
          input = 0;
        }
      } catch (e) { toast.error("Ulanib bo'lmadi"); return; }
    }
    const fd = new FormData();
    list.forEach(f => fd.append('files', f, f.name));
    if (input != null) fd.append('input', String(input));
    setUploading(true);
    try {
      const r = await authFetch(`${base}/upload`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409 && d.pick) { setPick({ files: list, inputs: d.inputs || [] }); return; }
      if (!r.ok) { toast.error(d.error || 'Yuklanmadi'); return; }
      setChooser(null); setPick(null);
      fastRef.current = Date.now() + 3000;
      toast.success(`📎 Yuklandi: ${(d.names || []).join(', ')}`);
    } catch (e) { toast.error("Ulanib bo'lmadi"); }
    finally { setUploading(false); }
  };
  const cancelChooser = async () => {
    setChooser(null);
    try { await post('/chooser-cancel', {}); } catch (e) { }
  };
  const onDragOver = (e) => {
    if (stopped || !e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
    e.preventDefault(); e.stopPropagation(); setDragOver(true);
  };
  const onDrop = (e) => {
    if (stopped || !e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    doUpload(e.dataTransfer.files);
  };

  const onPaste = (e) => {
    e.preventDefault();
    const t = (e.clipboardData && e.clipboardData.getData('text')) || '';
    if (!t) return;
    flushType(); agentHint();
    queueInput({ action: 'insert', text: t });
  };

  const ag = agent && agent.active ? agent : null;
  const border = ag ? (ag.paused ? 'border-amber-500' : 'border-sky-500') : stopped ? 'border-gray-700' : 'border-gray-800';
  const btn = fs ? 'px-2 py-1 bg-white/10 rounded hover:bg-white/20 text-sm transition text-white'
    : 'px-2 py-1 rounded text-sm transition bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100';
  const ar = wdAspect(device);

  return (
    <div className="flex flex-col items-center gap-2 shrink-0" style={style}>
      {/* Manzil qatori */}
      <div className={`shrink-0 w-full flex items-center gap-1.5 ${fs ? 'text-white' : ''}`}>
        <span className="text-xs font-semibold whitespace-nowrap" title={device.label} translate="no">🌐 B{device.slot + 1}</span>
        <button onClick={() => queueInput({ action: 'back' })} disabled={stopped} title="Orqaga" className={btn}>←</button>
        <button onClick={() => queueInput({ action: 'reload' })} disabled={stopped} title="Sahifani yangilash" className={btn}>↻</button>
        <button onClick={() => queueInput({ action: 'home' })} disabled={stopped} title={`Boshlang'ich sahifa: ${device.startUrl}`} className={btn}>⌂</button>
        <button onClick={() => fileRef.current && fileRef.current.click()} disabled={stopped || uploading}
          title="📎 Kompyuteringizdan fayl yuklash — sayt fayl so'rab turgan bo'lsa o'sha maydonga, aks holda sahifadagi fayl maydoniga (faylni ekranga sudrab tashlasangiz ham bo'ladi, 25 MB gacha)"
          className={chooser ? 'px-2 py-1 rounded text-sm transition bg-amber-500 text-black hover:bg-amber-400 animate-pulse' : btn}>{uploading ? '⏳' : '📎'}</button>
        <input ref={fileRef} type="file" multiple className="hidden" accept={chooser && chooser.accept ? chooser.accept : undefined}
          onChange={e => { doUpload(e.target.files); e.target.value = ''; }} />
        <input value={urlEdit != null ? urlEdit : url} onChange={e => setUrlEdit(e.target.value)}
          onFocus={e => { setUrlEdit(url); setTimeout(() => e.target.select(), 0); }} onBlur={() => setUrlEdit(null)}
          onKeyDown={e => { if (e.key === 'Enter') { const v = String(e.currentTarget.value || '').trim(); setUrlEdit(null); e.currentTarget.blur(); if (v) queueInput({ action: 'open', url: v }); } if (e.key === 'Escape') { setUrlEdit(null); e.currentTarget.blur(); } }}
          disabled={stopped} spellCheck={false} translate="no"
          title="Manzil — faqat loyihaning test manzillari ochiladi (prod rad etiladi). Nisbiy yo'l ham bo'ladi: /users"
          className={`min-w-0 flex-1 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 ${fs ? 'bg-white/10 text-white placeholder-gray-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'}`} />
      </div>
      {/* Ekran */}
      <div ref={boxRef} tabIndex={0} onKeyDown={onKeyDown} onPaste={onPaste} onFocus={() => setKbFocus(true)} onBlur={() => { flushType(); setKbFocus(false); }}
        onDragOver={onDragOver} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
        className={`relative border-4 rounded-lg overflow-hidden bg-black select-none touch-none outline-none ${border} ${stopped ? '' : 'cursor-pointer'}`}
        style={{ flex: '1 1 0', minHeight: 0, maxWidth: '100%', aspectRatio: `${ar}` }}
        onPointerDown={stopped ? undefined : onDown} onPointerUp={stopped ? undefined : onUp}>
        {frame && <img ref={frameRef} src={frame} alt="brauzer" draggable={false} className="w-full h-full object-contain pointer-events-none" />}
        {!frame && !stopped && <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">Yuklanmoqda...</div>}
        {dragOver && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-amber-400/30 border-4 border-dashed border-amber-400 text-white text-sm font-semibold pointer-events-none">
            📎 Qo'yib yuboring — fayl saytga yuklanadi
          </div>
        )}
        {chooser && !stopped && (
          <div onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()}
            className="absolute top-0 inset-x-0 z-20 flex items-center gap-2 flex-wrap bg-amber-400 text-black text-xs font-medium px-2 py-1.5 shadow cursor-default">
            <span>📎 Sayt fayl so'rayapti{chooser.multiple ? ' (bir nechta)' : ''}{chooser.accept ? ` · ${chooser.accept}` : ''}</span>
            <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}
              className="px-2 py-0.5 rounded bg-black/80 text-white hover:bg-black disabled:opacity-50">{uploading ? '⏳ Yuklanmoqda' : '📂 Faylni tanlang'}</button>
            <span className="opacity-80">yoki shu yerga sudrab tashlang</span>
            <button type="button" onClick={cancelChooser} title="Bekor qilish (sayt faylsiz davom etadi)" className="ml-auto px-1.5 rounded hover:bg-black/10">✕</button>
          </div>
        )}
        {pick && (
          <div onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()}
            className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 cursor-default">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-3 max-w-[90%] space-y-1.5">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">📎 Sahifada {pick.inputs.length} ta fayl maydoni — qaysi biriga?</div>
              {pick.inputs.map(inp => (
                <button key={inp.i} type="button" onClick={() => { const f = pick.files; setPick(null); doUpload(f, inp.i); }}
                  className="block w-full text-left text-xs px-2 py-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-primary-100 dark:hover:bg-primary-900">
                  #{inp.i + 1} {inp.label || 'nomsiz maydon'}{inp.accept ? ` · ${inp.accept}` : ''}{inp.multiple ? ' · bir nechta' : ''}
                </button>
              ))}
              <button type="button" onClick={() => setPick(null)} className="text-xs text-gray-500 hover:underline">Bekor</button>
            </div>
          </div>
        )}
        {stopped && (
          <div onPointerDown={e => e.stopPropagation()} className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/80 text-white text-center px-4 cursor-default">
            <span className="text-3xl">⏻</span>
            <span className="text-sm font-semibold" translate="no">«{device.label}»</span>
            <span className="text-[11px] opacity-70">Brauzer o'chiq — login saqlangan, yoqsangiz o'sha holatda ochiladi</span>
            <button onClick={(e) => { e.stopPropagation(); start(); }} disabled={busy} className="mt-1 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-semibold disabled:opacity-50">{busy ? '⏳' : '▶ Yoqish'}</button>
          </div>
        )}
        {ag && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 pointer-events-none max-w-[94%]">
            <div className={`px-2.5 py-1 rounded-full text-[11px] font-semibold shadow-lg backdrop-blur text-white flex items-center gap-1.5 whitespace-nowrap ${ag.paused ? 'bg-amber-600/90' : 'bg-sky-600/90'}`}>
              <span className={ag.paused ? '' : 'animate-pulse'}>{ag.paused ? '⏸ Boshqaruv sizda — agent kutmoqda' : '🤖 Agent ishlayapti'}</span>
              {!ag.paused && ag.last && <span className="opacity-80 truncate max-w-[260px]">· {ag.last.action}{ag.last.detail ? ` — ${String(ag.last.detail).slice(0, 40)}` : ''}</span>}
            </div>
          </div>
        )}
        {!stopped && frame && (
          <div className={`absolute left-2 top-2 text-[10px] px-1.5 py-0.5 rounded shadow pointer-events-none ${kbFocus ? 'bg-emerald-600 text-white' : 'bg-gray-900/60 text-gray-200'}`}>
            {kbFocus ? "⌨️ Yozing · ⌘V qo'yish · ⌘C nusxa" : '⌨️ Bosing — klaviatura ulanadi'}
          </div>
        )}
      </div>
      {ag && (
        <div className={`shrink-0 flex flex-wrap items-center justify-center gap-1.5 text-[11px] rounded-lg px-2 py-1 max-w-full ${ag.paused ? 'bg-amber-500/20 text-amber-700 dark:text-amber-100' : 'bg-sky-500/20 text-sky-700 dark:text-sky-100'}`}>
          <span className="font-semibold">{ag.paused ? "⏸ Agent to'xtatilgan" : '🤖 Agent ishlayapti'}</span>
          {agentWho(ag) && <span className="basis-full text-center opacity-90 truncate" title={agentWho(ag)} translate="no">{agentWho(ag)}</span>}
          <button onClick={() => onAgentControl && onAgentControl(device.key, ag.paused ? 'resume' : 'pause')}
            className={`px-2 py-0.5 rounded font-semibold text-white ${ag.paused ? 'bg-sky-600 hover:bg-sky-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
            {ag.paused ? '▶ Agentga qaytarish' : '✋ Boshqaruvni olish'}
          </button>
        </div>
      )}
      {/* Pastki qator */}
      <div className={`shrink-0 flex flex-wrap items-center justify-center gap-1.5 max-w-full px-1 ${fs ? 'text-white' : ''}`}>
        <span className="text-xs font-semibold truncate max-w-[180px]" translate="no" title={device.startUrl}>{device.label}</span>
        <span className="text-[10px] opacity-60">{device.size ? `${device.size.width}×${device.size.height}` : ''}</span>
        {!stopped && <button onClick={stop} disabled={busy || !!ag} title={ag ? "Agent ishlayotganda o'chirib bo'lmaydi" : "Brauzerni o'chirish (login saqlanadi) — server xotirasi bo'shaydi"} className={btn}>⏻</button>}
        {onToggleMain && (
          <button onClick={onToggleMain}
            title={isMain ? "Asosiy — bossangiz qurilmalar yana teng (shakliga mos) joylashadi" : "Shu brauzerni asosiy (katta) qilish — qolganlari o'ng ustunga o'tadi"}
            className={isMain ? 'px-2 py-1 rounded text-sm transition bg-yellow-500 text-black hover:bg-yellow-400' : btn}>{isMain ? '★' : '☆'}</button>
        )}
        {onRemoveFs && <button onClick={onRemoveFs} title={fs ? "To'liq ekrandan olib tashlash" : "To'liq ekranda (emulatorlar bilan yonma-yon) ochish"} className={fs ? 'px-2 py-1 bg-red-600/80 rounded hover:bg-red-600 text-sm transition text-white' : btn}>{fs ? '✕' : '⛶'}</button>}
      </div>
    </div>
  );
}

/** Qo'shish / tahrirlash oynasi. */
function WebDeviceModal({ authFetch, apiUrl, projectId, device, links, viewports, onClose, onSaved }) {
  const [label, setLabel] = useState(device ? device.label : '');
  const [startUrl, setStartUrl] = useState(device ? device.startUrl : '');
  const [viewport, setViewport] = useState(device ? device.viewport : 'desktop');
  // 🔑 Avtomatik kirish (2026-10-31): sahifa login'ga qaytsa brauzer o'zi kiradi. Parol serverda shifrlanadi va qaytarilmaydi —
  // bo'sh qoldirilsa eskisi qoladi; «o'chirish» belgisi bilan olib tashlanadi.
  const [autoLogin, setAutoLogin] = useState(device ? (device.autoLogin || '') : '');
  const [autoPass, setAutoPass] = useState('');
  const [clearPass, setClearPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${apiUrl}/web-devices`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, id: device ? device.id : undefined, label, startUrl, viewport,
          autoLogin, autoPassword: clearPass ? '' : (autoPass || undefined) }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Saqlanmadi');
      toast.success(device ? '✅ Saqlandi' : `✅ B${d.device.slot + 1} qo'shildi — ▶ Yoqish bilan oching`);
      onSaved(d.device);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!device || !window.confirm(`«${device.label}» brauzerini o'chirasizmi? Undagi login (cookie) ham o'chadi.`)) return;
    setBusy(true);
    try {
      const r = await authFetch(`${apiUrl}/web-devices/${device.id}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "O'chmadi");
      toast.success("🗑 O'chirildi");
      onSaved(null);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const inp = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';
  return (
    <div className="fixed inset-0 z-[75] bg-black/50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 dark:text-white">{device ? `🌐 B${device.slot + 1} — tahrirlash` : "🌐 Brauzer qo'shish"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl font-bold leading-none">×</button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">Serverdagi haqiqiy brauzer: siz ham, agent ham boshqaradi, login saqlanadi. Faqat loyihaning TEST manzillari ochiladi — production rad etiladi.</p>
        <label className="block text-sm text-gray-700 dark:text-gray-300">Nomi
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Test admin, Test Traccar, Dispetcher…" className={inp} maxLength={60} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-gray-300">Boshlang'ich manzil
          <input value={startUrl} onChange={e => setStartUrl(e.target.value)} placeholder="https://…test.khorezmai.dev" className={`${inp} font-mono`} spellCheck={false} translate="no" />
        </label>
        {(links || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {links.map(l => (
              <button key={l.url} type="button" onClick={() => { setStartUrl(l.url); if (!label.trim()) setLabel(l.name); }}
                className="text-xs px-2 py-1 rounded-md bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900" translate="no">🔗 {l.name}</button>
            ))}
          </div>
        )}
        <label className="block text-sm text-gray-700 dark:text-gray-300">Ko'rinish o'lchami
          <select value={viewport} onChange={e => setViewport(e.target.value)} className={inp}>
            {Object.entries(viewports || {}).map(([k, v]) => <option key={k} value={k}>{v.label || k}</option>)}
          </select>
        </label>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="text-sm font-medium text-gray-800 dark:text-gray-200">🔑 Avtomatik kirish <span className="text-xs font-normal text-gray-500 dark:text-gray-400">— sayt login sahifasiga qaytsa (chiqarib yuborsa) brauzer o'zi kiradi</span></div>
          <div className="grid grid-cols-2 gap-2">
            <input value={autoLogin} onChange={e => setAutoLogin(e.target.value)} placeholder="Login (email / telefon)" autoComplete="off" className={inp} translate="no" />
            <input value={autoPass} onChange={e => { setAutoPass(e.target.value); setClearPass(false); }} type="password" autoComplete="new-password"
              placeholder={device && device.hasAutoPass ? '•••••• saqlangan — o\'zgartirish' : 'Parol'} className={inp} />
          </div>
          {device && device.hasAutoPass && (
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <input type="checkbox" checked={clearPass} onChange={e => { setClearPass(e.target.checked); if (e.target.checked) setAutoPass(''); }} /> Avtomatik kirishni o'chirish (parolni o'chirish)
            </label>
          )}
          <p className="text-[11px] text-gray-400">Faqat shu brauzerning boshlang'ich sayti uchun ishlatiladi; parol shifrlanadi va hech qayerda ko'rsatilmaydi. 🧠 Agentga «B{device ? device.slot + 1 : 'N'} ga shu login/parol bilan kir» desangiz ham saqlaydi.</p>
        </div>
        <div className="flex items-center justify-between pt-1">
          {device ? <button onClick={remove} disabled={busy} className="px-3 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50">🗑 O'chirish</button> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600">Bekor</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700 font-semibold disabled:opacity-50">{busy ? '⏳' : '💾 Saqlash'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** /emu gridi ostidagi «🌐 Brauzerlar» bo'limi. Ro'yxat App.js'da (to'liq ekran ham ishlatadi) — bu yerda faqat ko'rinish. */
export function WebDevicesSection({ authFetch, apiUrl, projectId, data, reload, agents, onAgentControl, fsIds, onToggleFs }) {
  const [modal, setModal] = useState(null);   // null | 'new' | device
  const devices = (data && data.devices) || [];
  const max = (data && data.maxSlots) || 3;
  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="font-bold text-gray-900 dark:text-white">🌐 Brauzerlar <span className="text-xs font-normal text-gray-400">({devices.length}/{max})</span>
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">test admin, test Traccar… — siz ham, agent ham boshqaradi</span></h2>
        {devices.length < max && (
          <button onClick={() => setModal('new')} className="px-3 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg font-medium transition">➕ Brauzer qo'shish</button>
        )}
      </div>
      {devices.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          Hali brauzer yo'q. <button onClick={() => setModal('new')} className="text-emerald-600 dark:text-emerald-400 underline font-medium">Qo'shing</button> — masalan test admin paneli: agentga «dispetcher panelida planshetni ro'yxatga ol» desangiz, shu yerda ko'rib turasiz.
        </p>
      ) : (
        <div className="flex flex-wrap justify-center gap-4">
          {devices.map(d => (
            <div key={d.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
              <div className="flex justify-end mb-1">
                <button onClick={() => setModal(d)} title="Nomi / manzil / o'lcham / o'chirish" className="text-xs px-2 py-0.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700">⚙ Sozlash</button>
              </div>
              <WebDeviceScreen authFetch={authFetch} apiUrl={apiUrl} device={d} agent={agents && agents[d.key]} onAgentControl={onAgentControl} onChanged={reload}
                onRemoveFs={() => onToggleFs(d.id)} paused={(fsIds || []).includes(d.id)} style={{ width: 560, height: Math.round(560 / wdAspect(d)) + 96 }} />
              {(fsIds || []).includes(d.id) && <p className="text-[10px] text-center text-primary-600 dark:text-primary-400 mt-1">⛶ to'liq ekranda ochiq</p>}
            </div>
          ))}
        </div>
      )}
      {modal && (
        <WebDeviceModal authFetch={authFetch} apiUrl={apiUrl} projectId={projectId} device={modal === 'new' ? null : modal}
          links={data && data.links} viewports={data && data.viewports} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reload && reload(); }} />
      )}
    </div>
  );
}
